import { useState } from 'react';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_memberDTO,
  ApiResponse_searchResponseDTO_1,
  MemberDTO,
  SearchRequestDTO_memberSearchRequestDTO,
  MemberClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseMemberApi {
  create: (data: MemberDTO) => Promise<ApiResponse_memberDTO>;
  edit: (id: string, data: MemberDTO) => Promise<ApiResponse_memberDTO>;
  getById: (id: string) => Promise<ApiResponse_memberDTO>;
  search: (
    filters: SearchRequestDTO_memberSearchRequestDTO,
  ) => Promise<ApiResponse_searchResponseDTO_1>;
  remove: (id: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useMemberApi = (): UseMemberApi => {
  const apiClient = new MemberClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (data: MemberDTO): Promise<ApiResponse_memberDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.createMember(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error creating Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const edit = async (
    id: string,
    data: MemberDTO,
  ): Promise<ApiResponse_memberDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.updateMember(id, data);
    } catch (err: unknown) {
      handleHookError(err, 'Error updating Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (id: string): Promise<ApiResponse_memberDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.getMemberById(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const search = async (
    filters: SearchRequestDTO_memberSearchRequestDTO,
  ): Promise<ApiResponse_searchResponseDTO_1> => {
    setIsLoading(true);
    try {
      return await apiClient.searchMembers(filters);
    } catch (err: unknown) {
      handleHookError(err, 'Error searching Members', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      return await apiClient.deleteMember(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error deleting Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { create, edit, getById, search, remove, isLoading, error };
};
